import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, BookOpen, Star, CaretLeft, CaretRight, ShareNetwork, Printer, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStudent } from "@/context/StudentContext";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const TABS = [["harian", "Harian"], ["mingguan", "Mingguan"], ["bulanan", "Bulanan"]];
const AKHLAK_COLOR = { "Sangat Baik": "text-green-600", "Baik": "text-primary", "Cukup": "text-amber-600", "Kurang": "text-orange-600" };

export default function Laporan() {
  const { user } = useAuth();
  const { active, students: myKids } = useStudent();
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState(null);
  const [tab, setTab] = useState("harian");

  useEffect(() => {
    if (user?.role === "orangtua") {
      setSid(active?.id || null);
    } else {
      api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); });
    }
  }, [user, active]);

  const studentName = user?.role === "orangtua" ? active?.name : students.find((s) => s.id === sid)?.name;

  return (
    <div>
      <PageHeader title="Laporan Perkembangan" />
      <div className="px-5 pt-4">
        {user?.role !== "orangtua" && (
          <select
            data-testid="laporan-student-select"
            value={sid || ""}
            onChange={(e) => setSid(e.target.value)}
            className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-4 outline-none focus:border-primary"
          >
            {students.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.kelas}</option>)}
          </select>
        )}

        <div className="flex gap-2 bg-stone-100 p-1 rounded-full mb-5">
          {TABS.map(([v, l]) => (
            <button
              key={v}
              data-testid={`laporan-tab-${v}`}
              onClick={() => setTab(v)}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-primary text-white shadow" : "text-stone-500"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {!sid ? (
          <p className="text-center text-stone-400 mt-10">Belum ada data ananda.</p>
        ) : (
          <>
            {tab === "harian" && <Harian sid={sid} name={studentName} />}
            {tab === "mingguan" && <Mingguan sid={sid} name={studentName} />}
            {tab === "bulanan" && <Bulanan sid={sid} name={studentName} />}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- HARIAN ---------------- */
function Harian({ sid, name }) {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/daily?student_id=${sid}&date=${day}`)
      .then((r) => setReport(r.data[0] || null))
      .finally(() => setLoading(false));
  }, [sid, day]);

  const shift = (n) => {
    const d = new Date(day); d.setDate(d.getDate() + n);
    setDay(d.toISOString().slice(0, 10));
  };
  const fmt = new Date(day).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div>
      <div className="flex items-center justify-between bg-white rounded-2xl border border-stone-200/60 p-3 mb-4 shadow-sm">
        <button data-testid="harian-prev" onClick={() => shift(-1)} className="w-9 h-9 rounded-full bg-stone-50 flex items-center justify-center"><CaretLeft size={16} weight="bold" /></button>
        <span className="text-sm font-semibold text-stone-700">{fmt}</span>
        <button data-testid="harian-next" onClick={() => shift(1)} className="w-9 h-9 rounded-full bg-stone-50 flex items-center justify-center"><CaretRight size={16} weight="bold" /></button>
      </div>

      {loading ? (
        <p className="text-center text-stone-400 mt-8">Memuat...</p>
      ) : !report ? (
        <div className="flex flex-col items-center mt-10 text-stone-400">
          <WarningCircle size={44} weight="duotone" />
          <p className="mt-2 text-sm">Belum ada laporan untuk tanggal ini.</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <RowCard icon={CheckCircle} tone="text-green-600" label="Kehadiran" value={report.attendance_status} />
          <RowCard icon={BookOpen} tone="text-amber-600" label="Hafalan Hari Ini" value={report.hafalan_today || "—"} />
          <RowCard icon={Star} tone={AKHLAK_COLOR[report.akhlak] || "text-primary"} label="Perilaku / Akhlak" value={report.akhlak} />
          {report.note && (
            <div className="bg-white rounded-2xl border border-stone-200/60 p-4 shadow-sm">
              <p className="text-xs text-stone-400 mb-1">Catatan Guru</p>
              <p className="text-stone-700 text-sm leading-relaxed">{report.note}</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

function RowCard({ icon: Icon, tone, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center gap-3 shadow-sm">
      <div className="w-10 h-10 rounded-xl bg-stone-50 flex items-center justify-center">
        <Icon size={20} weight="fill" className={tone} />
      </div>
      <div className="flex-1">
        <p className="text-xs text-stone-400">{label}</p>
        <p className="font-semibold text-stone-800 text-sm">{value}</p>
      </div>
    </div>
  );
}

/* ---------------- MINGGUAN ---------------- */
function Mingguan({ sid, name }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/reports/weekly?student_id=${sid}`).then((r) => setData(r.data)); }, [sid]);
  if (!data) return <p className="text-center text-stone-400 mt-8">Memuat...</p>;

  const a = data.attendance;
  const fmtR = (s) => new Date(s).toLocaleDateString("id-ID", { day: "numeric", month: "short" });

  const share = async () => {
    const text = `Laporan Mingguan ${name}\nPeriode: ${fmtR(data.range.start)} - ${fmtR(data.range.end)}\nHadir: ${a.Hadir} | Izin: ${a.Izin} | Sakit: ${a.Sakit} | Alpha: ${a.Alpha}\nHafalan minggu ini: ${data.hafalan_week.join(", ") || "-"}`;
    try {
      if (navigator.share) await navigator.share({ title: "Laporan Mingguan", text });
      else { await navigator.clipboard.writeText(text); toast.success("Laporan disalin ke clipboard"); }
    } catch (e) {
      try { await navigator.clipboard.writeText(text); toast.success("Laporan disalin ke clipboard"); }
      catch (_) { toast.message("Berbagi dibatalkan"); }
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div className="bg-primary rounded-3xl p-5 text-white shadow-lg mb-4 relative overflow-hidden">
        <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full" />
        <p className="text-white/70 text-xs">Periode</p>
        <p className="font-heading font-bold text-lg">{fmtR(data.range.start)} – {fmtR(data.range.end)}</p>
      </div>

      <h3 className="font-heading font-semibold text-stone-800 mb-2">Rekap Kehadiran</h3>
      <div className="grid grid-cols-4 gap-2 mb-5">
        {[["Hadir", a.Hadir, "text-green-600"], ["Izin", a.Izin, "text-amber-600"], ["Sakit", a.Sakit, "text-orange-600"], ["Alpha", a.Alpha, "text-destructive"]].map(([l, v, t]) => (
          <div key={l} className="bg-white rounded-2xl border border-stone-200/60 p-3 text-center shadow-sm">
            <p className={`font-heading font-bold text-2xl ${t}`}>{v}</p>
            <p className="text-[10px] text-stone-400">{l}</p>
          </div>
        ))}
      </div>

      <h3 className="font-heading font-semibold text-stone-800 mb-2">Progress Hafalan Minggu Ini</h3>
      <div className="bg-white rounded-2xl border border-stone-200/60 p-4 mb-5 shadow-sm">
        {data.hafalan_week.length || data.hafalan_notes.length ? (
          <div className="flex flex-wrap gap-2">
            {[...new Set([...data.hafalan_week, ...data.hafalan_notes])].map((s, i) => (
              <span key={i} className="text-xs font-semibold bg-primary/10 text-primary px-3 py-1.5 rounded-full">{s}</span>
            ))}
          </div>
        ) : <p className="text-stone-400 text-sm">Belum ada hafalan tercatat minggu ini.</p>}
      </div>

      <h3 className="font-heading font-semibold text-stone-800 mb-2">Ringkasan Catatan Guru</h3>
      <div className="space-y-2 mb-6">
        {data.notes.length ? data.notes.map((n, i) => (
          <div key={i} className="bg-white rounded-2xl border border-stone-200/60 p-3.5 shadow-sm">
            <p className="text-xs text-primary font-semibold mb-1">{new Date(n.date).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "short" })} • Akhlak: {n.akhlak}</p>
            <p className="text-stone-700 text-sm">{n.note || n.hafalan_today || "Tidak ada catatan"}</p>
          </div>
        )) : <p className="text-stone-400 text-sm">Belum ada catatan harian minggu ini.</p>}
      </div>

      <div className="flex gap-3 no-print">
        <button data-testid="weekly-share-btn" onClick={share} className="flex-1 bg-primary text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <ShareNetwork size={18} weight="fill" /> Bagikan
        </button>
        <button data-testid="weekly-print-btn" onClick={() => window.print()} className="flex-1 bg-white border-2 border-primary text-primary font-semibold py-3 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Printer size={18} weight="fill" /> Unduh
        </button>
      </div>
    </motion.div>
  );
}

/* ---------------- BULANAN (Rapor Digital) ---------------- */
function Bulanan({ sid, name }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/reports/monthly?student_id=${sid}&month=${month}&year=${year}`).then((r) => setData(r.data));
  }, [sid, month, year]);

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
        {MONTHS.map((m, i) => (
          <button key={m} data-testid={`bulan-${i + 1}`} onClick={() => setMonth(i + 1)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold ${month === i + 1 ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>
            {m}
          </button>
        ))}
      </div>

      {!data ? <p className="text-center text-stone-400 mt-8">Memuat...</p> : (
        <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-br from-primary to-green-700 p-5 text-white text-center">
            <p className="text-white/70 text-xs uppercase tracking-widest">Rapor Digital</p>
            <h2 className="font-heading text-xl font-bold mt-1">{name}</h2>
            <p className="text-white/80 text-sm">{data.label}</p>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Kehadiran</p>
              <div className="flex items-end gap-2 mb-2">
                <span className="font-heading text-4xl font-bold text-primary">{data.attendance.hadir_pct}%</span>
                <span className="text-stone-400 text-sm mb-1">tingkat kehadiran</span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[["Hadir", data.attendance.Hadir], ["Izin", data.attendance.Izin], ["Sakit", data.attendance.Sakit], ["Alpha", data.attendance.Alpha]].map(([l, v]) => (
                  <div key={l} className="bg-stone-50 rounded-xl p-2 text-center">
                    <p className="font-bold text-stone-800">{v}</p>
                    <p className="text-[10px] text-stone-400">{l}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Hafalan Al-Qur'an</p>
              <div className="bg-amber-50 rounded-2xl p-4 flex items-center gap-3">
                <BookOpen size={30} weight="duotone" className="text-amber-600" />
                <div>
                  <p className="font-heading font-bold text-stone-800">{data.hafalan.total_completed} Surah dihafal</p>
                  <p className="text-stone-500 text-xs">+{data.hafalan.this_month} surah bulan ini</p>
                </div>
              </div>
            </div>

            {data.predikat && (
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Predikat & Perkembangan</p>
                <div className="bg-secondary/15 rounded-2xl p-4 mb-2 flex items-center gap-3">
                  <Star size={30} weight="fill" className="text-secondary" />
                  <p className="font-heading text-lg font-bold text-stone-800">{data.predikat.predikat}</p>
                </div>
                <div className="bg-white border border-stone-100 rounded-2xl divide-y divide-stone-100">
                  {data.predikat.aspects?.map((asp, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-stone-600 text-sm">{asp.name}</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} weight="fill" className={n <= asp.stars ? "text-secondary" : "text-stone-200"} />)}
                      </div>
                    </div>
                  ))}
                </div>
                {data.predikat.note && <p className="text-stone-600 text-sm mt-2 italic">"{data.predikat.note}"</p>}
              </div>
            )}

            {data.gallery?.length > 0 && (
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">Foto Kegiatan</p>
                <div className="grid grid-cols-3 gap-2">
                  {data.gallery.map((u, i) => (
                    <div key={i} className="aspect-square rounded-xl overflow-hidden bg-stone-100">
                      <img src={u} alt="kegiatan" loading="lazy" className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-5 pt-0 no-print">
            <button data-testid="monthly-print-btn" onClick={() => window.print()} className="w-full bg-primary text-white font-semibold py-3 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <Printer size={18} weight="fill" /> Unduh Rapor
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
